import {
  Button,
  Card,
  Form,
  Input,
  List,
  Space,
  Tag,
  Typography,
  message,
} from 'antd';
import { userLogin } from 'apis/user';
import { getLocalStorageFirstPage, LOCAL_STORAGE_TOKEN_KEY } from 'commons';
import {
  CachedAccount,
  formatTokenExpiry,
  getCachedAccounts,
  isTokenExpired,
  switchToCachedAccount,
} from 'commons/cached-accounts';
import { useCallback, useMemo, useState } from 'react';
import styles from './index.module.scss';
import { Trans } from 'react-i18next';
import LOCALS from 'commons/locals';
import i18n from 'i18n';

const Login = () => {
  const [cachedAccounts] = useState(() => getCachedAccounts());
  const usableAccounts = useMemo(
    () => cachedAccounts.filter((item) => !isTokenExpired(item.expiresAt)),
    [cachedAccounts],
  );

  const onFinish = useCallback(async (values: any) => {
    const { username, password } = values;
    const { token } = await userLogin({ username, password });
    localStorage.setItem(LOCAL_STORAGE_TOKEN_KEY, 'Bearer ' + token);
    window.location.href = getLocalStorageFirstPage(username);
  }, []);

  const handleQuickSwitch = (account: CachedAccount) => {
    try {
      switchToCachedAccount(account.adminId);
    } catch (err: any) {
      message.error(
        err?.message || String(i18n.t(LOCALS.switch_account_failed)),
      );
    }
  };

  return (
    <div className={styles.login}>
      <Card style={{ width: 384 }}>
        <h2 style={{ textAlign: 'center', marginBottom: 24, fontSize: 20 }}>
          GINZA XIAOMA ADMIN
        </h2>

        {usableAccounts.length > 0 ? (
          <div style={{ marginBottom: 24 }}>
            <Typography.Text strong>
              {i18n.t(LOCALS.quick_switch_logged_in_accounts)}
            </Typography.Text>
            <List
              style={{ marginTop: 8 }}
              bordered
              size="small"
              dataSource={usableAccounts}
              renderItem={(item) => (
                <List.Item
                  actions={[
                    <Button
                      key="switch"
                      type="link"
                      size="small"
                      onClick={() => handleQuickSwitch(item)}
                    >
                      {i18n.t(LOCALS.enter_account)}
                    </Button>,
                  ]}
                >
                  <List.Item.Meta
                    title={
                      <Space size={6}>
                        <span>{item.nickName || item.username}</span>
                        <Tag color="success">{i18n.t(LOCALS.token_valid)}</Tag>
                      </Space>
                    }
                    description={
                      <span style={{ fontSize: 12, color: '#8c8c8c' }}>
                        {item.username} · {formatTokenExpiry(item.expiresAt)}
                      </span>
                    }
                  />
                </List.Item>
              )}
            />
            <Typography.Paragraph
              type="secondary"
              style={{ marginTop: 12, marginBottom: 0, fontSize: 12 }}
            >
              {i18n.t(LOCALS.or_login_new_account_below)}
            </Typography.Paragraph>
          </div>
        ) : null}

        <Form
          layout="vertical"
          labelCol={{ span: 8 }}
          style={{ maxWidth: 600 }}
          onFinish={onFinish}
        >
          <Form.Item
            label={<Trans i18nKey={LOCALS.account} />}
            name="username"
            rules={[{ required: true, message: 'Please input your username!' }]}
          >
            <Input placeholder={i18n.t(LOCALS.please_enter) || ''} />
          </Form.Item>

          <Form.Item
            label={<Trans i18nKey={LOCALS.password} />}
            name="password"
            rules={[{ required: true, message: 'Please input your password!' }]}
          >
            <Input.Password placeholder={i18n.t(LOCALS.please_enter) || ''} />
          </Form.Item>

          <Form.Item>
            <Button block type="primary" htmlType="submit">
              <Trans i18nKey={LOCALS.log_in} />
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default Login;
